import type { ApiErrorBody } from '@shared/types';
import { OIDC_PATHS } from '@shared/oidc';

/**
 * 接口客户端。
 *
 * 所有接口返回 `{ ok, data | error }` 信封，这里把它拆平：成功直接给数据，
 * 失败抛 `ApiError`（带 `code` 供调用方分支，`message` 只用于展示）。
 *
 * 会话是同源 httpOnly Cookie，因此不需要（也不该）在前端碰令牌。
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: Record<string, string>;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details;
  }

  /** 校验类错误：返回字段名 → 原因的映射，供表单逐项标红。 */
  get fieldErrors(): Record<string, string> {
    return this.details ?? {};
  }
}

type UnauthorizedHandler = () => void;

/**
 * 接口地址。生产环境前端在 console.havorix.cn、接口在 api.havorix.cn，
 * 因此必须带上绝对地址；本地开发留空，靠 vite 代理走同源。
 *
 * 注意 `credentials: 'include'`：两个域名同属 haviorix.cn（同站不同源），
 * `SameSite=Lax` 的会话 Cookie 会照常带上，但 fetch 默认不带凭据，必须显式说明。
 */
export const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/+$/, '');

/** 把接口路径拼成可直接使用的 URL（也用于 /authorize 这类整页跳转）。 */
export function apiUrl(path: string): string {
  if (/^https?:\/\//.test(path)) return path;
  return `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * 判断一个路径是不是接口路径（OIDC 端点）。
 *
 * 用途：登录页拿到接口给的 `continue=/authorize?...` 之后，必须**整页跳转**到接口域名，
 * 不能用前端路由器导航 —— 那只会跳到 console 自己的同名路径上，然后又说未登录。
 */
export function isApiPath(path: string): boolean {
  const [pathname = ''] = path.split('?');
  return Object.values(OIDC_PATHS).some((endpoint) => pathname === endpoint);
}

let unauthorizedHandler: UnauthorizedHandler | null = null;

/** 全局 401 兜底：任何请求遇到会话失效都会把界面切回登录态。 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const init: RequestInit = {
    method,
    // 跨源（console → api）必须带凭据，否则会话 Cookie 不会被带上。
    credentials: 'include',
    headers: {},
  };

  if (body !== undefined) {
    (init.headers as Record<string, string>)['content-type'] = 'application/json';
    init.body = JSON.stringify(body);
  }

  const response = await fetch(apiUrl(path), init);

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let parsed: unknown;
  try {
    parsed = text === '' ? null : JSON.parse(text);
  } catch {
    throw new ApiError(response.status, {
      code: 'internal_error',
      message: `服务返回了无法解析的内容（HTTP ${response.status}）`,
    });
  }

  const envelope = parsed as { ok?: boolean; data?: T; error?: ApiErrorBody } | null;

  if (!response.ok || envelope?.ok !== true) {
    const error: ApiErrorBody = envelope?.error ?? {
      code: 'internal_error',
      message: `请求失败（HTTP ${response.status}）`,
    };
    // 会话失效时先通知全局，再抛给调用方 —— 调用方通常不需要自己处理 401。
    if (response.status === 401) unauthorizedHandler?.();
    throw new ApiError(response.status, error);
  }

  return envelope.data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body ?? {}),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

/** 表单提交时的通用兜底文案，避免把 500 的原始信息直接抛给用户。 */
export function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return '发生了未知错误，请稍后重试';
}
